/**
 * @note based on the example here: https://github.com/jaredpalmer/formik/blob/master/examples/MultistepWizard.js
 * simple enough that we don't need to install a library for this
 */
import React, { createContext, useContext, useState } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import { IconButton } from '@icecreamsocial/components';
import { RightArrowIcon } from '../SupportedIcons';

const WizardContext = createContext(null);

// Wizard is a single Formik instance whose children are each page of the
// multi-step form. The form is submitted on each forward transition (can only
// progress with valid input), whereas a backwards step is allowed with
// incomplete data. A snapshot of form state is used as initialValues after each
// transition. Each page has an optional submit handler, and the top-level
// submit is called when the final page is submitted.
export const Wizard = ({
	children,
	initialValues,
	onSubmit,
	lastStepLabel,
	continueLabel,
}) => {
	const [stepNumber, setStepNumber] = useState(0);
	const steps = React.Children.toArray(children);
	const [snapshot, setSnapshot] = useState(initialValues);

	const step = steps[stepNumber];
	const totalSteps = steps.length;
	const isLastStep = stepNumber === totalSteps - 1;

	const next = (values) => {
		setSnapshot(values);
		setStepNumber(Math.min(stepNumber + 1, totalSteps - 1));
	};

	const back = (values) => {
		setSnapshot(values);
		setStepNumber(Math.max(stepNumber - 1, 0));
	};

	const handleSubmit = async (values, bag) => {
		if (step.props.onSubmit) {
			await step.props.onSubmit(values, bag);
		}
		if (isLastStep) {
			return onSubmit(values, bag);
		} else {
			bag.setTouched({});
			next(values);
		}
	};

	return (
		<WizardContext.Provider value={{ next, back, stepNumber }}>
			<Formik
				initialValues={snapshot}
				onSubmit={handleSubmit}
				validate={step?.props?.validate}
				validationSchema={step?.props?.validationSchema}
				enableReinitialize={true}
			>
				{(formik) => (
					<Form>
						{step}
						<IconButton
							type='submit'
							iconName='ic_lock_open'
							id={`step${step}`}
							disabled={!formik.isValid || formik.isSubmitting}
							backgroundColor='accent'
							textColor='bodyInverse'
							elWidth='100%'
							padding='16px 32px'
							gridColumns='1fr 3fr 1fr'
							marginTop='lg'
							marginBottom='lg'
							data-slate-cta
						>
							<div /> {/* grid layout hax */}
							<IconButton.Text display='block'>
								{isLastStep ? lastStepLabel : continueLabel}
							</IconButton.Text>
							<IconButton.Icon as={RightArrowIcon} justifyContent='right' />
						</IconButton>
					</Form>
				)}
			</Formik>
		</WizardContext.Provider>
	);
};

Wizard.Step = ({ children }) => {
	const formik = useFormikContext();
	const ctx = useContext(WizardContext);
	const renderFn =
		typeof children === 'function' || children instanceof Function;

	return renderFn ? children(formik, ctx) : children;
};
